import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, Sparkles, Upload, FileText, X, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { AiInvoiceResult } from "./types";
import MaintenanceAIReviewDialog, { ResolvedLine, ResolvedHeader } from "./MaintenanceAIReviewDialog";

interface LineItem {
  system_name: string;
  work_type_name: string;
  cost: string;
  is_labour: boolean;
  is_gst: boolean;
  notes: string;
}

interface Props {
  vehicles: { vehicle_number: string; registration: string | null }[];
  systems: string[];
  workTypes: string[];
  onSaved: () => void;
}

const normalizePlate = (p: string) => (p || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

type PlateMatch =
  | { status: "none" }
  | { status: "matched"; plate: string; vehicle_number: string }
  | { status: "unmatched"; plate: string };

const emptyLine = (): LineItem => ({ system_name: "", work_type_name: "", cost: "", is_labour: false, is_gst: false, notes: "" });

// RLS / permission errors come back with code 42501 or a "row-level security" message.
const isRlsError = (e: any): boolean => {
  const code = String(e?.code || "");
  const msg = (e?.message || "").toLowerCase();
  return code === "42501" || msg.includes("row-level security") || msg.includes("violates row-level") || msg.includes("permission denied");
};

// "unknown column" style schema errors so we can gracefully retry with core columns.
const isColumnError = (e: any): boolean => {
  const msg = (e?.message || "").toLowerCase();
  return msg.includes("column") || msg.includes("schema cache") || msg.includes("does not exist");
};

const MaintenanceRecordForm: React.FC<Props> = ({ vehicles, systems, workTypes, onSaved }) => {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [serviceDate, setServiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [supplier, setSupplier] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [odometer, setOdometer] = useState("");
  const [registration, setRegistration] = useState("");
  const [gst, setGst] = useState("");
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);

  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [aiResult, setAiResult] = useState<AiInvoiceResult | null>(null);
  const [plateMatch, setPlateMatch] = useState<PlateMatch>({ status: "none" });

  // Look up a plate against the vehicles table. Optionally auto-select the matched fleet #.
  const lookupPlate = (plate: string, autoSelect: boolean): PlateMatch => {
    const norm = normalizePlate(plate);
    if (!norm) {
      setPlateMatch({ status: "none" });
      return { status: "none" };
    }
    const found = vehicles.find((v) => v.registration && normalizePlate(v.registration) === norm);
    if (found) {
      const m: PlateMatch = { status: "matched", plate: plate.trim(), vehicle_number: found.vehicle_number };
      setPlateMatch(m);
      if (autoSelect) setVehicleNumber(found.vehicle_number);
      return m;
    }
    const m: PlateMatch = { status: "unmatched", plate: plate.trim() };
    setPlateMatch(m);
    return m;
  };

  const handleRegistrationChange = (val: string) => {
    setRegistration(val);
    lookupPlate(val, false);
  };

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const updateLine = (idx: number, patch: Partial<LineItem>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx: number) => setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));

  const toBase64 = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  const readInvoiceWithAI = async () => {
    if (!file) {
      toast({ title: "Upload an invoice first", variant: "destructive" });
      return;
    }
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type.includes("pdf") || file.name.toLowerCase().endsWith(".pdf");
    if (!isImage && !isPdf) {
      toast({ title: "Unsupported file", description: "Please upload a photo/scan (JPG, PNG) or a PDF invoice.", variant: "destructive" });
      return;
    }
    setReading(true);
    try {
      const dataUrl = await toBase64(file);
      const { data, error } = await supabase.functions.invoke("read-maintenance-invoice", {
        body: { fileDataUrl: dataUrl, mimeType: file.type, fileName: file.name, systems, workTypes },
      });
      if (error) throw error;
      if (data?.error && !data?.result) throw new Error(data.error);
      const result = data?.result as AiInvoiceResult | undefined;
      if (!result) throw new Error("No data extracted");

      setAiResult(result);
      setReviewOpen(true);
      toast({ title: "Invoice read", description: `${(result.line_items || []).length} item(s) detected. Review them before applying.` });
    } catch (e: any) {
      toast({ title: "Could not read invoice", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setReading(false);
    }
  };

  const applyReviewed = (reviewed: ResolvedLine[], header: ResolvedHeader) => {
    if (header.supplier) setSupplier(header.supplier);
    if (header.invoiceNumber) setInvoiceNumber(header.invoiceNumber);
    if (header.invoiceDate) setServiceDate(header.invoiceDate);
    if (header.registration) {
      setRegistration(header.registration);
      // Auto look up the AI-read plate against the fleet and pre-select the vehicle.
      const m = lookupPlate(header.registration, true);
      if (m.status === "matched") {
        toast({ title: "Vehicle matched", description: `Plate ${m.plate} → Fleet #${m.vehicle_number} (auto-selected).` });
      } else if (m.status === "unmatched") {
        toast({ title: "Plate not recognised", description: `${m.plate} doesn't match any vehicle. Please pick the fleet # manually.`, variant: "destructive" });
      }
    }

    const mapped: LineItem[] = reviewed.map((r) => ({
      system_name: r.system_name,
      work_type_name: r.work_type_name,
      cost: r.cost,
      is_labour: r.is_labour,
      is_gst: r.is_gst,
      notes: r.notes,
    }));

    // GST now arrives as a normal line item, so any GST line clears the
    // standalone GST field to avoid double-counting.
    if (mapped.some((m) => m.is_gst)) setGst("");

    // Replace the form lines if they are still empty, otherwise append.
    const existing = lines.filter((l) => l.system_name || l.work_type_name || l.cost || l.notes);
    setLines(existing.length ? [...existing, ...mapped] : (mapped.length ? mapped : [emptyLine()]));
    toast({ title: "Applied", description: `${mapped.length} item(s) and the confirmed invoice details were added.` });
  };


  const reset = () => {
    setVehicleNumber("");
    setServiceDate(new Date().toISOString().slice(0, 10));
    setSupplier("");
    setInvoiceNumber("");
    setOdometer("");
    setRegistration("");
    setGst("");
    setPlateMatch({ status: "none" });
    setLines([emptyLine()]);
    setFile(null);
  };

  // Insert maintenance records, retrying without the optional is_gst column if the
  // schema doesn't have it yet. Returns the inserted rows (with ids) for linking.
  const insertRecords = async (records: any[]): Promise<{ id: string }[]> => {
    let res = await supabase.from("maintenance_records").insert(records).select("id");
    if (res.error && isColumnError(res.error)) {
      const stripped = records.map(({ is_gst, ...rest }) => rest);
      res = await supabase.from("maintenance_records").insert(stripped).select("id");
    }
    if (res.error) throw res.error;
    return (res.data || []) as { id: string }[];
  };

  const save = async () => {
    if (!vehicleNumber.trim()) {
      toast({ title: "Vehicle required", description: "Select the vehicle the work was done on.", variant: "destructive" });
      return;
    }
    const validLines = lines.filter((l) => l.system_name || l.work_type_name || l.cost || l.notes);
    const gstValue = Number(gst);
    const hasGst = gst !== "" && Number.isFinite(gstValue) && gstValue > 0;
    if (validLines.length === 0 && !hasGst) {
      toast({ title: "Add at least one item", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const vn = vehicleNumber.trim();

      // 1) Build the records. GST is now a normal item (its own line/work type).
      const records: any[] = validLines.map((l) => ({
        vehicle_number: vn,
        system_name: l.is_gst ? "GST" : (l.system_name || null),
        work_type_name: l.is_gst ? "GST" : (l.work_type_name || null),
        service_date: serviceDate,
        cost: l.cost === "" ? null : Number(l.cost),
        is_labour: l.is_gst ? false : l.is_labour,
        is_gst: !!l.is_gst,
        odometer: odometer === "" ? null : Number(odometer),
        supplier: supplier.trim() || null,
        notes: l.notes.trim() || null,
        invoice_id: null,
      }));

      // The standalone GST field is a shortcut — only add it as a GST record when
      // none of the line items already represent GST (avoids double-counting).
      const linesHaveGst = validLines.some((l) => l.is_gst);
      if (hasGst && !linesHaveGst) {
        records.push({
          vehicle_number: vn,
          system_name: "GST",
          work_type_name: "GST",
          service_date: serviceDate,
          cost: Math.round(gstValue * 100) / 100,
          is_labour: false,
          is_gst: true,
          odometer: odometer === "" ? null : Number(odometer),
          supplier: supplier.trim() || null,
          notes: "GST charged over the full invoice",
          invoice_id: null,
        });
      }


      // 2) Insert the core records FIRST. This is the critical write.
      let insertedIds: { id: string }[] = [];
      try {
        insertedIds = await insertRecords(records);
      } catch (e: any) {
        if (isRlsError(e)) {
          throw new Error(
            "Saving was blocked by a row-level security policy on the maintenance_records table. The maintenance tables need an INSERT policy that allows this app to write. Please enable insert access for the maintenance_records (and maintenance_invoices) tables."
          );
        }
        throw e;
      }

      // 3) File upload + invoice row are best-effort: if storage/invoice RLS blocks
      //    them, the records are already saved, so we just warn rather than fail.
      let invoiceWarning: string | null = null;
      if (file) {
        try {
          const ext = file.name.split(".").pop() || "dat";
          const path = `${vn}/${Date.now()}.${ext}`;
          const { error: upErr } = await supabase.storage.from("vehicle-invoices").upload(path, file, { upsert: true });
          if (upErr) throw upErr;

          const exclGstTotal = validLines.filter((l) => !l.is_gst).reduce((s, l) => s + (Number(l.cost) || 0), 0);
          const lineGst = validLines.filter((l) => l.is_gst).reduce((s, l) => s + (Number(l.cost) || 0), 0);
          const totalGst = lineGst + (hasGst && !linesHaveGst ? gstValue : 0);
          const totalAmount = exclGstTotal + totalGst;

          const invoiceRow: any = {
            vehicle_number: vn,
            file_path: path,
            file_name: file.name,
            supplier: supplier.trim() || null,
            invoice_number: invoiceNumber.trim() || null,
            invoice_date: serviceDate || null,
            registration: registration.trim() || null,
            total_amount: totalAmount || null,
            gst_amount: totalGst > 0 ? Math.round(totalGst * 100) / 100 : null,
          };

          let invRes = await supabase.from("maintenance_invoices").insert([invoiceRow]).select("id").single();

          if (invRes.error && isColumnError(invRes.error)) {
            const { gst_amount, ...coreRow } = invoiceRow;
            invRes = await supabase.from("maintenance_invoices").insert([coreRow]).select("id").single();
          }
          if (invRes.error) throw invRes.error;

          const invoiceId = invRes.data?.id ?? null;
          if (invoiceId && insertedIds.length) {
            await supabase
              .from("maintenance_records")
              .update({ invoice_id: invoiceId })
              .in("id", insertedIds.map((r) => r.id));
          }
        } catch (e: any) {
          invoiceWarning = isRlsError(e)
            ? "the invoice attachment couldn't be saved (storage/maintenance_invoices is blocked by a security policy)."
            : `the invoice attachment couldn't be saved (${e?.message ?? "unknown error"}).`;
        }
      }

      if (invoiceWarning) {
        toast({
          title: "Records saved (invoice not attached)",
          description: `${records.length} record(s) were logged against ${vn}, but ${invoiceWarning}`,
        });
      } else {
        toast({
          title: "Saved",
          description: `${records.length} maintenance record(s)${hasGst ? " (incl. GST)" : ""} logged against ${vn}.`,
        });
      }
      reset();
      onSaved();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const lineTotal = lines.reduce((s, l) => s + (Number(l.cost) || 0), 0);
  const gstNum = Number(gst);
  const hasGstPreview = gst !== "" && Number.isFinite(gstNum) && gstNum > 0;
  const grandTotal = lineTotal + (hasGstPreview ? gstNum : 0);

  return (
    <>
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Log Maintenance / Repair
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Header fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Label>Vehicle (Fleet #) *</Label>
              {plateMatch.status === "matched" && (
                <Badge className="bg-green-600/20 text-green-400 border border-green-600/40 gap-1 text-[11px]">
                  <CheckCircle2 className="w-3 h-3" /> Matched {plateMatch.plate}
                </Badge>
              )}
              {plateMatch.status === "unmatched" && (
                <Badge className="bg-amber-600/20 text-amber-400 border border-amber-600/40 gap-1 text-[11px]">
                  <AlertTriangle className="w-3 h-3" /> Plate not in fleet
                </Badge>
              )}
            </div>
            <Select value={vehicleNumber} onValueChange={setVehicleNumber}>
              <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {vehicles.map((v) => (
                  <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                    {v.vehicle_number}{v.registration ? ` · ${v.registration}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Date</Label>
            <Input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Odometer (km, optional)</Label>
            <Input type="number" value={odometer} onChange={(e) => setOdometer(e.target.value)} placeholder="e.g. 124300" />
          </div>
          <div className="space-y-1">
            <Label>Supplier / Service Centre (optional)</Label>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. City Auto" />
          </div>
          <div className="space-y-1">
            <Label>Invoice # (optional)</Label>
            <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2034" />
          </div>
          <div className="space-y-1">
            <Label>Number plate / Rego (optional)</Label>
            <Input value={registration} onChange={(e) => handleRegistrationChange(e.target.value)} placeholder="e.g. PZU288" />
            {plateMatch.status === "matched" && (
              <p className="text-[11px] text-green-400 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Matches Fleet #{plateMatch.vehicle_number}
              </p>
            )}
            {plateMatch.status === "unmatched" && (
              <p className="text-[11px] text-amber-400 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> No vehicle with this plate — pick the fleet # manually
              </p>
            )}
          </div>
          <div className="space-y-1">
            <Label>GST ($, charged over full invoice)</Label>
            <Input type="number" step="0.01" value={gst} onChange={(e) => setGst(e.target.value)} placeholder="e.g. 11.09" />
            <p className="text-[11px] text-muted-foreground">Stored separately as its own GST line on the record.</p>
          </div>
        </div>

        {/* Invoice upload + AI */}
        <div className="rounded-xl border border-yellow-900/30 bg-black/20 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="text-yellow-500 flex items-center gap-2">
              <FileText className="w-4 h-4" /> Attach invoice (optional)
            </Label>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => document.getElementById("maint-invoice-file")?.click()}>
                <Upload className="w-4 h-4 mr-2" /> {file ? "Change file" : "Choose file"}
              </Button>
              <Button size="sm" onClick={readInvoiceWithAI} disabled={!file || reading} className="golden-button">
                {reading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                {reading ? "Reading..." : "Read with AI"}
              </Button>
            </div>
            <input
              id="maint-invoice-file"
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {file && (
            <div className="flex items-center gap-3">
              {previewUrl && file.type.startsWith("image/") ? (
                <img src={previewUrl} alt="invoice" className="h-16 w-16 object-cover rounded border border-border" />
              ) : (
                <FileText className="h-10 w-10 text-muted-foreground" />
              )}
              <span className="text-sm text-muted-foreground truncate flex-1">{file.name}</span>
              <Button variant="ghost" size="sm" onClick={() => setFile(null)}><X className="w-4 h-4" /></Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Upload a photo/scan or PDF and let AI read it. You'll get a preview where each detected item (COF, service, parts, labour) is highlighted with a suggested system, work type, cost and notes — plus the GST charged over the whole invoice — to accept, edit or decline before saving.
          </p>
        </div>

        {/* Line items */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Items</Label>
            <Button variant="outline" size="sm" onClick={addLine}><Plus className="w-4 h-4 mr-2" /> Add item</Button>
          </div>

          {lines.map((line, idx) => (
            <div key={idx} className="rounded-lg border border-border bg-background/40 p-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-3 space-y-1">
                  <Label className="text-xs">Vehicle System</Label>
                  <Select value={line.system_name} onValueChange={(v) => updateLine(idx, { system_name: v })}>
                    <SelectTrigger><SelectValue placeholder="System" /></SelectTrigger>
                    <SelectContent className="max-h-[260px]">
                      {systems.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3 space-y-1">
                  <Label className="text-xs">Work Type</Label>
                  <Select value={line.work_type_name} onValueChange={(v) => updateLine(idx, { work_type_name: v, is_labour: v.toLowerCase() === "labour" ? true : line.is_labour })}>
                    <SelectTrigger><SelectValue placeholder="Work" /></SelectTrigger>
                    <SelectContent className="max-h-[260px]">
                      {workTypes.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Cost ($)</Label>
                  <Input type="number" step="0.01" value={line.cost} onChange={(e) => updateLine(idx, { cost: e.target.value })} placeholder="0.00" />
                </div>
                <div className="md:col-span-3 grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 rounded-md border border-border bg-background/40 px-2 py-2 text-xs h-10">
                    <Checkbox
                      checked={line.is_labour}
                      onCheckedChange={(c) => updateLine(idx, { is_labour: Boolean(c), is_gst: Boolean(c) ? false : line.is_gst })}
                    />
                    <span>Labour</span>
                  </label>
                  <label className="flex items-center gap-2 rounded-md border border-border bg-background/40 px-2 py-2 text-xs h-10">
                    <Checkbox
                      checked={line.is_gst}
                      onCheckedChange={(c) => {
                        const on = Boolean(c);
                        const gstType = workTypes.find((w) => w.toLowerCase() === "gst");
                        const gstSystem = systems.find((s) => s.toLowerCase() === "gst");
                        updateLine(idx, on
                          ? { is_gst: true, is_labour: false, system_name: gstSystem || "GST", work_type_name: gstType || "GST" }
                          : { is_gst: false });
                      }}
                    />
                    <span>GST</span>
                  </label>
                </div>

                <div className="md:col-span-1 flex justify-end">
                  <Button variant="destructive" size="sm" onClick={() => removeLine(idx)} disabled={lines.length <= 1}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea value={line.notes} onChange={(e) => updateLine(idx, { notes: e.target.value })} placeholder="e.g. Front LH headlight bulb replaced" rows={2} />
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 border-t border-border pt-4">
          <div className="text-sm space-x-3">
            <span className="text-muted-foreground">Subtotal: </span>
            <span className="font-medium">
              ${lineTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            {hasGstPreview && (
              <>
                <span className="text-muted-foreground">+ GST: </span>
                <span className="font-medium text-emerald-300">
                  ${gstNum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </>
            )}
            <span className="text-muted-foreground">Total: </span>
            <span className="font-semibold text-green-400">
              ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={reset}>Clear</Button>
            <Button onClick={save} disabled={saving} className="golden-button">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? "Saving..." : "Save Maintenance"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>

    <MaintenanceAIReviewDialog
      open={reviewOpen}
      onOpenChange={setReviewOpen}
      previewUrl={previewUrl}
      fileMime={file?.type ?? null}
      fileName={file?.name ?? null}
      result={aiResult}
      systems={systems}
      workTypes={workTypes}
      onApply={applyReviewed}
    />
    </>
  );
};

export default MaintenanceRecordForm;
